'use client';

import React, { useMemo } from 'react';
import DOMPurify from 'isomorphic-dompurify';

import { twMerge } from 'utilities/src/twMerge';

import SyntaxHighlighter from 'ui/src/components/SyntaxHighlighter';

import { useBodySections } from 'utils/postBodyUtils';

import type { HandlePageDataType } from 'app/[slug]/data/handlePageData';
import ShareRail from 'app/[slug]/components/ShareRail';

import styles from './PostBody.module.scss';
import BannerCta from './BannerCta';
import useBannerCtaExperiment from './BannerCta/hooks/useBannerCtaExperiment';

/**
 * Utilities
 */

// handlePageData gives every <h2> the id the table of contents links to. DOMPurify would otherwise drop ids that
// shadow document properties (a heading called "Images" gets id="images"), breaking that link.
DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
  if (node.nodeName === 'H2' && data.attrName === 'id') {
    // eslint-disable-next-line no-param-reassign
    data.forceKeepAttr = true;
  }
});

const SANITIZE_OPTIONS = {
  ADD_TAGS: ['iframe'],
  ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'target', 'referrerpolicy'],
};

/**
 * Types
 */

export type PostBodyProps = {
  content: string;
  title: string;
  url: string;
  hideSharing?: boolean;
  bannerCta?: HandlePageDataType['bannerCta'];
  className?: string;
};

/**
 * Component
 */

function PostBody({ content, title, url, hideSharing, bannerCta, className }: PostBodyProps) {
  const { bodySections } = useBodySections({ formattedArticleContents: content });

  // React 19 re-applies innerHTML whenever the `dangerouslySetInnerHTML` object changes identity, even if the HTML is
  // the same. Keeping one object per section stops the article DOM from being rebuilt (and losing focus) on re-renders.
  const sectionMarkup = useMemo(
    () =>
      bodySections.map((section) =>
        section.html ? { __html: DOMPurify.sanitize(section.html, SANITIZE_OPTIONS) } : null,
      ),
    [bodySections],
  );

  const slug = url.match(/[^/]+\/?$/)?.[0] ?? '';

  const { experimentEnabled } = useBannerCtaExperiment({ slug });

  return (
    <div className={twMerge('mdS:flex-row flex flex-col gap-6 pb-12 pt-4', className)}>
      <ShareRail url={url} title={title} hideSharing={hideSharing} />

      <div className={twMerge('site-paragraph-xl text-site-neutrals-700 min-w-0 flex-1', styles.root)}>
        {bodySections.map((section, index) => (
          <React.Fragment key={section.key}>
            {!!section.html && (
              // eslint-disable-next-line react/no-danger
              <span dangerouslySetInnerHTML={sectionMarkup[index] ?? undefined} />
            )}

            {!!section.banner && bannerCta && experimentEnabled && (
              <div className="my-8">
                <BannerCta slug={slug} {...bannerCta} />
              </div>
            )}

            {!!section.code && (
              <SyntaxHighlighter
                language={section.language}
                code={section.code}
                className="max-w-full pb-8"
                customStyle={{ fontSize: '1rem' }}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

export default React.memo(PostBody);
